import { Directive, Input, Optional, SkipSelf } from '@angular/core';
import { DisplayableError } from "../services/dtos/displayable-error";
import { camelToSnakeCase } from "../utils/string-utils";
import { StatusCode } from "../models/status-code.model";

export interface FieldValidationListener {
  id: string;
  reasons: string[];
  reset: () => void;
  resetStatusCodes: () => void;
  updateStatusCodes: (statusCode: StatusCode, forceDisplay: boolean) => void;
  updateFormErrors: (dirtyCheck: boolean) => void;
  shouldDisplayStatusCodes: () => boolean;
  alwaysDisplayStatusCode: boolean
}

export interface StatusCodesListener {
  id: string;
  reset: () => void;
  updateStatusCodes: (validationResult: StatusCode[]) => void;
  hideIfOtherDisplays: boolean;
}

export interface FormErrorsListener {
  id: string;
  reset: () => void;
  updateFormErrors: (errorKeys: string[]) => void;
  hideIfOtherDisplays: boolean;
}

@Directive({
  selector: '[valgDomainValidationAnnouncer]',
  exportAs: 'valgDomainValidationAnnouncer',
})
export class DomainValidationResultAnnouncerDirective {
  @Input() isolated = false;
  private readonly fieldValidationListenerRegistry: Map<string, FieldValidationListener> = new Map<string, FieldValidationListener>();
  private readonly statusCodesListenerRegistry: Map<string, StatusCodesListener> = new Map<string, StatusCodesListener>();
  private readonly formErrorsListenerRegistry: Map<string, FormErrorsListener> = new Map<string, FormErrorsListener>();
  private readonly errorParamMapper: Map<string, any> = new Map<string, FormErrorsListener>();

  private formErrorKeys: string[] = [];
  private activeStatusCodes: StatusCode[] =[];

  constructor(@Optional() @SkipSelf() readonly parentAnnouncer?: DomainValidationResultAnnouncerDirective) {
  }

  get hasFormErrors() {
    return this.formErrorKeys.length > 0;
  }

  get hasStatusCodes() {
    return this.activeStatusCodes.length > 0;
  }

  hasSpecificInvalidityReasons(invalidityReasons: string[]) {
    return this.activeStatusCodes.filter(statusCode => invalidityReasons.includes(statusCode.reason)).length > 0
  }
  
  registerFieldValidationListener(listener: FieldValidationListener): void {
    this.parentAnnouncer?.registerFieldValidationListener(listener);
    this.fieldValidationListenerRegistry.set(listener.id, listener);
    if (this.activeStatusCodes.length > 0) {
      this.announceFieldValidationListeners(this.activeStatusCodes)
    }
  }

  deregisterFieldValidationListener(listener: FieldValidationListener): void {
    this.parentAnnouncer?.deregisterFieldValidationListener(listener);
    this.fieldValidationListenerRegistry.delete(listener.id);
  }

  registerStatusCodesListener(listener: StatusCodesListener): void {
    if (!this.isolated) {
      this.parentAnnouncer?.registerStatusCodesListener(listener);
    }
    this.statusCodesListenerRegistry.set(listener.id, listener);
  }

  deregisterStatusCodesListener(listener: StatusCodesListener): void {
    this.parentAnnouncer?.deregisterStatusCodesListener(listener);
    this.statusCodesListenerRegistry.delete(listener.id);
  }

  registerFormErrorsListener(listener: FormErrorsListener): void {
    this.parentAnnouncer?.registerFormErrorsListener(listener);
    this.formErrorsListenerRegistry.set(listener.id, listener);
  }

  deregisterFormErrorsListener(listener: FormErrorsListener): void {
    this.parentAnnouncer?.deregisterFormErrorsListener(listener);
    this.formErrorsListenerRegistry.delete(listener.id);
  }

  announceStatusCodes(
    hardValidationStatusCodes: StatusCode[],
    includeAll = true,
    liveValidationStatusCodes: StatusCode[] = [],
    distinct = true
  ) {
    if (!this.isolated) {
      this.parentAnnouncer?.announceStatusCodes(hardValidationStatusCodes, includeAll, liveValidationStatusCodes)
    }
    this.fieldValidationListenerRegistry.forEach((x) => {
      x.resetStatusCodes();
    });
    if (distinct) {
      hardValidationStatusCodes = this.getDistinctStatusCodes(hardValidationStatusCodes);
    }

    if (includeAll) {
      this.announceStatusCodesListeners(hardValidationStatusCodes);
    } else {
      const domainValidationListeners = Array.from(this.fieldValidationListenerRegistry.values());
      const enabledStatusCodes = hardValidationStatusCodes.filter(message => {
        const listener = domainValidationListeners.find(domainListener => {
          return domainListener.reasons.includes(message.reason)
        });
        return listener?.shouldDisplayStatusCodes() ?? true;
      })
     this.announceStatusCodesListeners(enabledStatusCodes);
    }
    this.announceFieldValidationListeners(liveValidationStatusCodes, includeAll)
  }

  pushFormErrorKey(...errorKeys: string[]) {
    for (const key of errorKeys) {
      if (this.formErrorKeys.findIndex((x) => x === key) == -1) {
        this.formErrorKeys.push(key);
      }
    }
    this.parentAnnouncer?.pushFormErrorKey(...errorKeys);
  }

  setErrorParam(param: any, errorKey: string) {
    this.errorParamMapper.set(errorKey, param);
  }

  getErrorParam(errorKey: string) {
    return this.errorParamMapper.get(errorKey);
  }

  removeFormErrorKey(...errorKeys: string[]) {
    for (const key of errorKeys) {
      const keyIndex = this.formErrorKeys.findIndex((x) => x === key);
      if (keyIndex >= 0) {
        this.formErrorKeys.splice(keyIndex, 1);
      }
    }
    this.parentAnnouncer?.removeFormErrorKey(...errorKeys);
  }

  reset() {
    this.formErrorKeys = [];
    this.activeStatusCodes = [];
    this.fieldValidationListenerRegistry.forEach((x) => {
      x.reset();
    });
    this.statusCodesListenerRegistry.forEach((x) => {
      x.reset();
    });
    this.formErrorsListenerRegistry.forEach((x) => {
      x.reset();
    });
  }

  receiveBusinessValidationError(businessValidationError: DisplayableError[],
    filterFunction: ((val: string) => boolean) | undefined = undefined){
    let errorKeys = businessValidationError.flatMap(x => this.mapDisplayableErrorToString(x));
    if (filterFunction) {
      errorKeys = errorKeys.filter((value) => filterFunction(value))
    }
    this.formErrorKeys = errorKeys;
    this.announceFormErrorsListeners();
  }

  public announceFormErrors() {
    this.parentAnnouncer?.announceFormErrors();
    this.fieldValidationListenerRegistry.forEach((registry) => {
      registry.updateFormErrors(false)
    });
    this.announceFormErrorsListeners();
  }

  public announceDirtyFormFieldErrors() {
    this.fieldValidationListenerRegistry.forEach((registry) => {
      registry.updateFormErrors(true);
    });
  }

  public announceCustomFormErrors(customErrorKey?: string) {
    this.parentAnnouncer?.announceFormErrors();
    this.fieldValidationListenerRegistry.forEach((registry) => {
      registry.updateFormErrors(false)
    });
    if (customErrorKey) {
      this.announceCustomFormErrorsListener(customErrorKey);
    }
  }

  private announceStatusCodesListeners(statusCodes: StatusCode[]) {
    this.activeStatusCodes = statusCodes;
    let multipleListeners = this.statusCodesListenerRegistry.size > 1;
    this.statusCodesListenerRegistry.forEach((x) => {
      x.reset();
      if (!multipleListeners || !x.hideIfOtherDisplays) {
        x.updateStatusCodes(statusCodes);
      }
    });
  }

  public announceFormErrorsListeners() {
    let multipleListeners = this.statusCodesListenerRegistry.size > 1;
    this.formErrorsListenerRegistry.forEach((x) => {
      x.reset();
      if (!multipleListeners || !x.hideIfOtherDisplays) {
        x.updateFormErrors([ ...this.formErrorKeys ]);
      }
    });
  }

  public announceCustomFormErrorsListener(customErrorKey: string) {{
    let multipleListeners = this.statusCodesListenerRegistry.size > 1;
    this.formErrorsListenerRegistry.forEach((x) => {
      x.reset();
      if (!multipleListeners || !x.hideIfOtherDisplays) {
        x.updateFormErrors([ customErrorKey ]);
      }
    });
  }}

  public overrideStatusCodeFilter(alwaysDisplay = true){
    this.fieldValidationListenerRegistry.forEach(x=>x.alwaysDisplayStatusCode = alwaysDisplay);
  }

  private announceFieldValidationListeners(statusCodes: StatusCode[], forceDisplay = false) {
    statusCodes.forEach((statusCode) => {
      this.fieldValidationListenerRegistry.forEach((listener) => {
        if (statusCode.reason && listener.reasons.includes(statusCode.reason)) {
          listener.updateStatusCodes(statusCode, forceDisplay);
        }
      });
    });
  }

  private mapDisplayableErrorToString(businessValidationError: DisplayableError) {
    if (
      businessValidationError.errorParameters === undefined ||
      businessValidationError.errorParameters?.length == 0 ||
      businessValidationError.errorKey.includes("business_validation")) {
      return [ businessValidationError.errorMessage ];
    } else {
      return businessValidationError.errorParameters?.map((param) => {

        return [
          businessValidationError.errorMessage,
          camelToSnakeCase(param.parameterName ?? ""),
          param.parameterMessageKey ]
          .filter(x => x)
          .join(".")
      }) ?? []
    }
  }

  private getDistinctStatusCodes(statusCodes: StatusCode[]): StatusCode[] {
    return statusCodes.filter(
      (item, index, array) => array.findIndex(i => i.reason === item.reason && i.reasonType === item.reasonType) === index
    );
  }
}
